import { Buffer } from "buffer";
import pica from "pica";
import React, { useCallback, useEffect, useState } from "react";
import { BiCrop } from "react-icons/bi";
import { FaRedoAlt, FaUndoAlt } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import profileAvatar from "../../assets/img/profile-avatar-gray.jpg";
import ImagesService from "../../services/ImagesService";
import "./Image.css";
import { useIndexedDb } from "../../contexts/indexedDb";
import { Cropper } from "./Cropper";
import { dataUrlToFile } from "../../utils/file";

const createImage = (url) =>
	new Promise((resolve, reject) => {
		const img = new window.Image();
		img.addEventListener("load", () => resolve(img));
		img.addEventListener("error", (error) => reject(error));
		img.setAttribute("crossOrigin", "anonymous");
		img.src = url;
	});

export async function enhanceImageQuality(imageSrc, returnBlob = false, isPng = true) {
	const image = await createImage(imageSrc);
	const offScreenCanvas = document.createElement("canvas");
	offScreenCanvas.width = image.width;
	offScreenCanvas.height = image.height;

	const picaInstance = pica();
	await picaInstance.resize(image, offScreenCanvas, {
		quality: 1,
		unsharpAmount: 80,
		unsharpThreshold: 2,
	});

	return new Promise((resolve, reject) => {
		offScreenCanvas.toBlob(
			(blob) => {
				if (!blob) {
					reject(new Error("Canvas está vazio"));
					return;
				}
				blob.name = "enhanced.png";
				if (returnBlob) return resolve(blob);
				const enhancedDataUrl = window.URL.createObjectURL(blob);
				resolve(enhancedDataUrl);
			},
			`image/${isPng ? 'png' : 'jpeg'}`,
			1,
		);
	});
}

function Image({ isPng = true, onShowCropper, ...props }) {
	const [imageBinary, setImageBinary] = useState(null);
	const [rotation, setRotation] = useState(0);
	const [showCropper, setShowCropper] = useState(false);
	const { loadImage: loadCachedImage, lastSessionData } = useIndexedDb();

	const getImage = async (imgId) => {
		if (!imgId) return;
		try {
			const response = await ImagesService.getImage(imgId);
			if (!response.data) return;
			const responseImage = await ImagesService.getRawImage(response.data.replace("[:STORAGE_ADDRESS:]", window.location.hostname));
			const binaryData = responseImage.data;
			const base64Data = new Buffer.from(binaryData, "binary").toString(
				"base64",
			);
			const originalDataUrl = `data:image/${isPng ? 'png' : 'jpeg'};base64,${base64Data}`;
			const enhancedDataUrl = await enhanceImageQuality(originalDataUrl, false, isPng);
			setImageBinary(enhancedDataUrl);
		} catch (error) {
			console.error("Erro ao carregar imagem do servidor:", error);
		}
	};

	const getInitials = (fullName) => {
		if (!fullName) return "";
		const parts = fullName.trim().split(" ");
		if (parts.length === 1) {
			return parts[0].charAt(0).toUpperCase();
		}
		return (
			parts[0].charAt(0).toUpperCase() +
			parts[parts.length - 1].charAt(0).toUpperCase()
		);
	};

	useEffect(() => {
		if (props.cached && props.cacheKey) {
			(async () => {
				const cachedImage = await loadCachedImage(props.cacheKey);
				setImageBinary(cachedImage);
			})();
		}
	}, [lastSessionData]);

	useEffect(() => {
		if (props.cached && props.cacheKey) {
			return;
		}
		if (
			props.imgId &&
			!props.imgId.startsWith("blob:") &&
			!props.imgId.startsWith("data:")
		) {
			getImage(props.imgId);
		} else {
			setImageBinary(null);
		}
	}, [props.imgId, props.forceReload, props.cacheKey, props.cached]);

	let currentImageSrc = null;
	if (
		props.imgId &&
		(props.imgId.startsWith("blob:") || props.imgId.startsWith("data:"))
	) {
		currentImageSrc = props.imgId;
	} else if (imageBinary) {
		currentImageSrc = imageBinary;
	}

	const rotateLeft = () => {
		setRotation((prev) => prev - 90);
	};

	const rotateRight = () => {
		setRotation((prev) => prev + 90);
	};

	const removeImage = (event) => {
		event.stopPropagation();
		setImageBinary(null);
		if (props.onFileChanged) {
			props.onFileChanged(null);
		}
		if (props.setUrlImg) {
			props.setUrlImg(null);
		}
	};

	const finalizeCrop = async (cropDataAsUrl) => {
		try {
			props.onFileChanged?.(await dataUrlToFile(cropDataAsUrl));
			setImageBinary(cropDataAsUrl);
			setShowCropper(false);
			onShowCropper?.(false);
		} catch (error) {
			console.error("Erro no crop:", error);
		}
	};

	const handleCrop = () => {
		if (!currentImageSrc) return;
		setShowCropper(true);
		onShowCropper?.(true);
	};

	let content = null;

	if (props.isLogo) {
		const logoSrc =
			currentImageSrc ||
			(props.defaultImage ? props.defaultImage : profileAvatar);
		content = (
			<div className={`logo-container ${imageBinary ? "" : "logo-default"} ${props.className || ""}`}>
				<img
					src={logoSrc}
					alt={props.alt}
					onClick={props.onClick}
					onError={(e) => {
						e.target.src = props.defaultImage
							? props.defaultImage
							: profileAvatar;
					}}
				/>
			</div>
		);
	} else if (props.isProfile && props.name) {
		let avatarUrl = currentImageSrc;
		if (!avatarUrl && props.avatar) {
			avatarUrl =
				props.avatar.startsWith("http") || props.avatar.startsWith("data:")
					? props.avatar
					: `http://localhost:4000/api/files?imageUrl=${encodeURIComponent(
						props.avatar,
					)}`;
		}

		if (avatarUrl) {
			content = (
				<div className={`profile-avatar-container ${props.className || ""}`}>
					<img
						src={avatarUrl}
						alt={props.alt}
						onClick={props.onClick}
						onError={(e) => {
							e.target.src = props.defaultImage
								? props.defaultImage
								: profileAvatar;
						}}
					/>
				</div>
			);
		} else {
			const initials = getInitials(props.name);
			content = (
				<div
					className={`profile-placeholder ${props.className || ""}`}
					onClick={props.onClick}
					title={props.alt}
				>
					{initials}
				</div>
			);
		}
	} else if (showCropper && currentImageSrc) {
		content = (
			<Cropper
				src={currentImageSrc}
				onCancel={() => {
					setShowCropper(false);
					onShowCropper?.(false);
				}}
				onConfirm={finalizeCrop}
				isPng={isPng}
			/>
		);
	} else if (currentImageSrc) {
		if (props.onlyImage) {
			content = (
				<div className={`static-image-container ${props.className || ""}`}>
					<img
						src={currentImageSrc}
						alt={props.alt}
						onClick={props.onClick}
						style={{ transform: `rotate(${rotation}deg)` }}
						onError={(e) => {
							e.target.src = props.defaultImage
								? props.defaultImage
								: profileAvatar;
						}}
					/>
				</div>
			);
		} else {
			content = (
				<div className={`image-edit-container ${props.className || ""}`}>
					<img
						src={currentImageSrc}
						alt={props.alt}
						onClick={props.onClick}
						style={{ transform: `rotate(${rotation}deg)` }}
						onError={(e) => {
							e.target.src = props.defaultImage
								? props.defaultImage
								: profileAvatar;
						}}
					/>
					<div className="image-edit-controls">
						<button
							type="button"
							className="icon-button"
							onClick={removeImage}
							title="Remover imagem"
						>
							<MdDelete />
						</button>
						<button
							type="button"
							className="icon-button"
							onClick={rotateLeft}
							title="Rotacionar à esquerda"
						>
							<FaUndoAlt />
						</button>
						<button
							type="button"
							className="icon-button"
							onClick={rotateRight}
							title="Rotacionar à direita"
						>
							<FaRedoAlt />
						</button>
						<button
							type="button"
							className="icon-button"
							onClick={handleCrop}
							title="Cortar imagem"
						>
							<BiCrop />
						</button>
					</div>
				</div>
			);
		}
	} else {
		content = (
			<div className={`static-image-container ${props.className || ""}`}>
				<img
					src={props.defaultImage ? props.defaultImage : profileAvatar}
					alt={props.alt}
					onClick={props.onClick}
				/>
			</div>
		);
	}
	return content;
}

export default Image;
